import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  ACCOUNT_NAV,
  isClientSettingsPath,
  settingsTabFromSearch,
} from './ClientSidebar.jsx';
import { orgUserProfilePhoto } from '../lib/profileAvatar.js';

function initialsFromUser(user) {
  const name = String(user?.displayName || user?.name || user?.fullName || '').trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  const email = String(user?.email || '').trim();
  if (email) return email.slice(0, 2).toUpperCase();
  return 'DJ';
}

export default function HeaderAccountMenu({
  user,
  onSignOut,
  settingsPath = '',
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const menuId = useId();
  const location = useLocation();
  const initials = initialsFromUser(user);
  const photoUrl = useMemo(
    () =>
      orgUserProfilePhoto(
        { email: user?.email, avatarDataUrl: user?.avatarDataUrl },
        { email: user?.email, avatarDataUrl: user?.avatarDataUrl }
      ),
    [user?.avatarDataUrl, user?.email]
  );
  const settingsTab = settingsTabFromSearch(location.search);

  function accountItemActive(item) {
    if (item.id === 'profile') {
      return isClientSettingsPath(location.pathname) && settingsTab === 'profile';
    }
    if (item.id === 'settings') {
      return isClientSettingsPath(location.pathname) && settingsTab !== 'profile';
    }
    if (item.id === 'guides') {
      return (
        location.pathname.startsWith('/client/guides') ||
        location.pathname.startsWith('/client/learn')
      );
    }
    return location.pathname.startsWith(String(item.to).split('?')[0]);
  }

  useEffect(() => {
    function onDocClick(e) {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <div className={`header-account${open ? ' is-open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="header-account-toggle"
        aria-label="Account menu"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
      >
        <span
          className={`header-account-avatar${photoUrl ? ' has-photo' : ''}`}
          aria-hidden="true"
        >
          {photoUrl ? (
            <img src={photoUrl} alt="" className="header-account-avatar-image" />
          ) : (
            initials
          )}
        </span>
      </button>

      {open ? (
        <div className="header-account-menu" id={menuId} role="menu" aria-label="Account">
          <p className="header-account-label">
            {user?.email || user?.name || 'Account'}
          </p>
          {ACCOUNT_NAV.map((item) => (
            <NavLink
              key={item.id}
              to={item.id === 'settings' && settingsPath ? settingsPath : item.to}
              role="menuitem"
              className={() =>
                accountItemActive(item) ? 'header-account-item is-active' : 'header-account-item'
              }
              onClick={() => setOpen(false)}
            >
              {item.label}
            </NavLink>
          ))}
          <div className="header-account-divider" role="separator" />
          <button
            type="button"
            className="header-account-item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onSignOut?.();
            }}
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}
